import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import "./CastingDirectorDashboard.css";
import { fetchCastingDirectorDashboardApi, removeFollowerApi  } from "../../../services/api";

import CastingDashboardHeader from "./components/CastingDashboardHeader";
import CastingKpiGrid from "./components/CastingKpiGrid";
import CastingApplicantsChart from "./components/CastingApplicantsChart";
import CastingActiveRolesList from "./components/CastingActiveRolesList";
import CastingRecentApplicationsTable from "./components/CastingRecentApplicationsTable";
import FollowersModal from "./components/FollowersModal";

const CastingDirectorDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFollowersModal, setShowFollowersModal] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await fetchCastingDirectorDashboardApi();
      setDashboardData(response.data.data);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFollower = async (talentId, talentName) => {
    try {
      const res = await removeFollowerApi(talentId);

      if (res.data?.success || res.success) {
        setDashboardData((prev) => {
          if (!prev) return prev;
          const updatedFollowers = (prev.followers || []).filter((f) => f.id !== talentId);
          return {
            ...prev,
            kpis: { ...prev.kpis, followersCount: updatedFollowers.length },
            followers: updatedFollowers
          };
        });

        Swal.fire({
          icon: "success",
          title: "Follower Removed",
          text: `Removed ${talentName} from your followers list.`,
          width: "300px",
          background: "#131824",
          color: "#ffffff",
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (err) {
      console.error("Remove follower error:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Failed to remove follower",
        width: "300px",
        background: "#131824",
        color: "#ffffff"
      });
    }
  };

  const filteredApplications =
    activeTab === "all"
      ? dashboardData?.recentApplications || []
      : dashboardData?.recentApplications?.filter((item) => {
          const status = String(item?.status || '').toLowerCase();
          if (activeTab === "pending") {
            return status === "pending" || status === "applied";
          }
          if (activeTab === "shortlisted") {
            return (
              status === "shortlisted" ||
              status === "selected"
            );
          }
          return true;
        }) || [];

  const COLOR_MAP = {
    Shortlisted: "#00c781",
    Selected: "#00c781",
    Pending: "#ffb020",
    Applied: "#ffb020",
    Resubmit: "#a855f7",
    RESUBMIT: "#a855f7",
    Rejected: "#ff4d4f"
  };

  const pieChartData = (dashboardData?.chartData || []).filter(
    (item) => item.value > 0
  );

  const totalApplicantsCount = (dashboardData?.chartData || []).reduce(
    (acc, curr) => acc + (Number(curr.value) || 0), 
    0
  );

  if (loading) {
    return (
      <div className="cdd-loading-container">
        <Spinner animation="border" variant="danger" />
        <p className="mt-3">Loading Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="cdd-wrapper">
      <div className="cdd-container">
        <CastingDashboardHeader
          dashboardData={dashboardData}
          setShowFollowersModal={setShowFollowersModal}
          navigate={navigate}
        />

        <CastingKpiGrid
          dashboardData={dashboardData}
        />

        <div className="cdd-middle-grid">
          <CastingApplicantsChart
            dashboardData={dashboardData}
            pieChartData={pieChartData}
            totalApplicantsCount={totalApplicantsCount}
            COLOR_MAP={COLOR_MAP}
          />

          <CastingActiveRolesList
            dashboardData={dashboardData}
            navigate={navigate}
          />
        </div>

        <CastingRecentApplicationsTable
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          filteredApplications={filteredApplications}
          navigate={navigate}
        />
      </div>

      <FollowersModal
        showFollowersModal={showFollowersModal}
        setShowFollowersModal={setShowFollowersModal}
        dashboardData={dashboardData}
        handleRemoveFollower={handleRemoveFollower}
        navigate={navigate}
      />
    </div>
  );
};

export default CastingDirectorDashboard;